import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  Identity,
  OrderRowSupplyEntry,
  OrderRowSupplyEntryTypeEnum,
  QuoteSummary,
  SupplyEntryService,
} from '@libs/core/api';
import { ModalController } from '@ionic/angular';
import { BehaviorSubject, take } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoadingService, ToastService } from '../../services';

@UntilDestroy()
@Component({
  selector: 'ui-quote-supply-create',
  templateUrl: './quote-supply-create.component.html',
  styleUrls: ['./quote-supply-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteSupplyCreateComponent implements OnInit {
  @Input() identity!: Identity;
  @Input() detail!: QuoteSummary;
  @Input() supplyEntry: OrderRowSupplyEntry | undefined;
  @Input() maxOrders: number | undefined = undefined;
  @Input() disableDelete = false;

  selectedDate: string | undefined = undefined;
  isTotalDelivery = true;
  quantity: number | undefined = undefined;
  attachment: { url: string; name: string } | null = null;
  isReadOnly = false;

  isSaving = new BehaviorSubject<boolean>(false);

  constructor(
    private modalController: ModalController,
    private supplyEntryService: SupplyEntryService,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    if (this.supplyEntry) {
      this.quantity = this.supplyEntry.quantity;
      this.selectedDate = this.supplyEntry.arrivalDate;
      this.isTotalDelivery =
        this.supplyEntry.orderRowSupplyEntryType ===
        OrderRowSupplyEntryTypeEnum.Total;
      this.attachment =
        this.supplyEntry.fileUrl && this.supplyEntry.fileName
          ? {
              url: this.supplyEntry.fileUrl,
              name: this.supplyEntry.fileName,
            }
          : null;
      this.isReadOnly = true;
    }
  }

  close() {
    this.modalController.dismiss();
  }

  save() {
    if (!this.quantity || !this.selectedDate) {
      return;
    }

    this.isSaving.next(true);
    this.supplyEntryService
      .createSupplyEntry({
        body: {
          arrivalDate: this.selectedDate,
          orderRowSupplyEntryType: this.isTotalDelivery
            ? OrderRowSupplyEntryTypeEnum.Total
            : OrderRowSupplyEntryTypeEnum.Partial,
          quantity: this.quantity,
          fileName: this.attachment?.name,
          fileUrl: this.attachment?.url,
          orderRequestId: this.detail.orderRequest?.id,
          orderRowNumber: 1, //TODO: Multi-row support not implemented yet
        },
      })
      .pipe(
        take(1),
        tap(() => {
          this.modalController.dismiss('success');
        }),
        this.loadingService.withLoading(),
        this.toastService.catchErrorWithToast(),
        finalize(() => {
          this.isSaving.next(false);
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  onDateChange($event: string) {
    this.selectedDate = $event;
  }

  onAttach($event: { url: string; name: string } | null) {
    this.attachment = $event;
  }

  delete() {
    if (this.disableDelete) {
      return;
    }
    this.isSaving.next(true);

    if (this.supplyEntry?.id) {
      this.supplyEntryService
        .deleteSupplyEntry({ id: this.supplyEntry.id })
        .pipe(
          take(1),
          tap(() => {
            this.modalController.dismiss('deleted');
          }),
          this.loadingService.withLoading(),
          this.toastService.catchErrorWithToast(),
          finalize(() => {
            this.isSaving.next(false);
          }),
          untilDestroyed(this)
        )
        .subscribe();
    }
  }

  get isFormValid(): boolean {
    return (
      !!this.quantity &&
      !!this.selectedDate &&
      this.quantity > 0 &&
      (!this.maxOrders || this.quantity <= this.maxOrders)
    );
  }
}
